import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import ReactECharts from "echarts-for-react";
import { getNavResult } from "../../selectors/widget.js";
import Block from "../Block";
import Select from "react-select";
import { round } from "lodash";

const Graph = ({ module }) => {
  const { leftDropDown, series, cityIndex, county, weighting } = module;
  const apiResult = useSelector((state) => getNavResult(state));
  const [mergedOptions, setMergedOptions] = useState(null);

  const [leftDropDownOptions, setLeftDropDownOptions] = useState(null);
  const [selectedLeftValue, setSelectedLeftValue] = useState(null);
  const [periodOptions, setPeriodOptions] = useState(null);
  const [selectedPeriod, setSelectedPeriod] = useState(null);

  const [apiDataset, setApiDataset] = useState(null);
  const [filteredDataset, setFilteredDataset] = useState(null);

  useEffect(() => {
    if (apiResult) {
      let arr = apiResult.map((item) => {
        return {
          ...item,
          data: item.data,
        };
      });
      let mergeArray = [];
      arr.forEach((item) => {
        mergeArray = [...mergeArray, ...item.data];
      });

      let weightedIndexes = weighting.map((item) => {
        return parseInt(item.cityIndex);
      });

      let filtered = mergeArray.filter((item) => weightedIndexes.includes(item.cityIndex));
      let templateCityIndex = mergeArray.find((item) => item.cityIndex === cityIndex);
      let reduced = filtered.reduce((acc, { cityIndex, name, year, month, population, percent }) => {
        const key = `${year}-${month}`;
        let currentCityWeighting = weighting.find((weightItem) => parseInt(weightItem.cityIndex) === cityIndex);
        acc[key] = acc[key] || { year, month, population: 0, percent: 0 };
        let populationWeight = population * parseFloat(currentCityWeighting.weightPercentage);
        let percentWeight = percent * parseFloat(currentCityWeighting.weightPercentage);

        acc[key].population += populationWeight;
        acc[key].percent += percentWeight;

        return acc;
      }, {});

      let combinedIndex = Object.keys(reduced).map((key) => {
        return {
          ...templateCityIndex,
          year: reduced[key].year,
          month: reduced[key].month,
          population: round(reduced[key].population),
          percent: round(reduced[key].percent, 1),
        };
      });

      let weigthedArray = mergeArray.map((item) => {
        let newIndex = combinedIndex.find(
          (newCity) =>
            item.cityIndex === newCity.cityIndex && item.month === newCity.month && item.year === newCity.year
        );
        return newIndex ? newIndex : item;
      });
      setApiDataset(weigthedArray);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [apiResult]);

  useEffect(() => {
    if (apiDataset && leftDropDown) {
      if (leftDropDown === "places") {
        let filtered = apiDataset.filter((item) => item.cityIndex !== cityIndex);
        const unique = [...new Set(filtered.map((item) => item.name))];
        let sorted = unique.sort((a, b) => (a > b ? 1 : -1));
        let parsed = parseOptions(sorted);
        setLeftDropDownOptions(parsed);
      }
    }
  }, [apiDataset, leftDropDown, cityIndex]);

  useEffect(() => {
    if (filteredDataset) {
      let data = filteredDataset;
      let labels = [...new Set(data.map((item) => item.year))];
      let seriesData = [];
      if (series) {
        seriesData = series.map((item) => {
          let seriesItem = data.filter((i) => {
            if (item.group === "leftDropDown") {
              if (selectedLeftValue) {
                return i.name === selectedLeftValue.value;
              }
            }

            if (typeof item.group === "string" || item.group instanceof String) {
              return i.name === item.group;
            }
            return i.cityIndex === cityIndex;
          });

          if (selectedPeriod.value === "All") {
            let years = [...new Set(seriesItem.map((item) => item.year))];
            let grouped = years.map((item) => {
              let resultByYear = seriesItem.filter((i) => i.year === item);
              let numMonths = resultByYear.length;
              let totalPopulation = Math.round(resultByYear.reduce((a, v) => (a = a + v.population), 0) / numMonths);
              let totalPercent = resultByYear.reduce((a, v) => (a = a + v.percent), 0) / numMonths;
              return {
                year: item,
                population: totalPopulation,
                cityIndex: resultByYear[0].cityIndex,
                name: resultByYear[0].name,
                percent: round(totalPercent, 2),
              };
            });
            let sorted = grouped.sort((a, b) => a.year - b.year);
            let seriesItemData = sorted.map((i) => {
              return { name: i.name, value: round(i.percent, 2), population: i.population };
            });

            return {
              data: seriesItemData,
              type: "line",
              name: item.label ? item.label : seriesItem[0] ? seriesItem[0].name : "",
            };
          }

          let result = seriesItem.filter((i) => i.year === selectedPeriod.value);
          labels = [...new Set(result.map((item) => item.month))];
          let seriesItemData = result.map((i) => {
            return { name: i.name, value: round(i.percent, 2), population: i.population };
          });

          return {
            data: seriesItemData,
            type: "line",
            name: item.label ? item.label : seriesItem[0] ? seriesItem[0].name : "",
          };
        });
      }

      let nObj = {
        ...JSON.parse(options),
        tooltip: {
          trigger: "axis",
          order: "valueDesc",
          formatter: function (params) {
            let tooltip = `${params[0].axisValue} <br/>`;
            params.forEach((item) => {
              tooltip += `${item.marker} ${item.seriesName} <br/>
                 Prosent: ${item.value}%  <br/>
                 Antall: ${item.data.population}<br/>`;
            });
            return tooltip;
          },
        },
        legend: {
          show: true,
          data: seriesData.map((item) => item.name),
        },
        xAxis: {
          type: "category",
          data: labels,
          axisLabel: {
            interval: 0,
            rotate: 30, //If the label names are too long you can manage this by rotating the label.
          },
        },
        yAxis: {
          type: "value",
          min: 0,
          axisLabel: {
            show: true,
            formatter: function (value) {
              return value + "%";
            },
          },
        },
        series: seriesData,
      };

      setMergedOptions(nObj);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filteredDataset, selectedLeftValue]);

  useEffect(() => {
    if (selectedLeftValue && leftDropDown === "places") {
      const filterPeriod = (place) => {
        let filtered = apiDataset.filter(
          (item) => item.name === place || item.cityIndex === cityIndex || item.name === "Norge"
        );
        let sorted = filtered.sort((a, b) => a.year - b.year);
        setFilteredDataset(sorted);
      };
      filterPeriod(selectedLeftValue.value);
    }
  }, [selectedLeftValue, apiDataset, leftDropDown, cityIndex, selectedPeriod]);

  useEffect(() => {
    if (leftDropDownOptions && !selectedLeftValue) {
      setSelectedLeftValue(leftDropDownOptions.filter((item) => item.value === county)[0]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [leftDropDownOptions, selectedLeftValue]);

  useEffect(() => {
    if (!selectedPeriod && !periodOptions && apiDataset) {
      const unique = [...new Set(apiDataset.map((item) => item.year))];
      let sorted = unique.sort((a, b) => (a < b ? 1 : -1));
      let parsed = parseOptions(sorted);
      let total = { label: `${sorted.slice(-1)}-${sorted[0]}`, value: "All" };
      parsed.unshift(total);
      setPeriodOptions(parsed);
      setSelectedPeriod(parsed[0]);
    }
  }, [selectedPeriod, periodOptions, apiDataset]);

  useEffect(() => {
    if (apiDataset && !leftDropDown) {
      setLeftDropDownOptions(null);
      setSelectedLeftValue(null);
      let sorted = apiDataset.sort((a, b) => a.year - b.year);
      setFilteredDataset(sorted);
    }
  }, [apiDataset, leftDropDown, cityIndex]);

  if (mergedOptions) {
    return (
      <Block>
        <div className="selectbox">
          {selectedPeriod && periodOptions && (
            <div className="col-lg-6">
              <Select
                options={periodOptions}
                defaultValue={periodOptions[0]}
                onChange={(d) => {
                  setSelectedPeriod(d);
                }}
                value={selectedPeriod}
              />
            </div>
          )}
          {leftDropDown && leftDropDownOptions && (
            <div className="col-lg-6">
              <Select
                options={leftDropDownOptions}
                defaultValue={leftDropDownOptions[0]}
                onChange={(d) => {
                  setSelectedLeftValue(d);
                }}
                value={selectedLeftValue}
              />
            </div>
          )}
        </div>

        <ReactECharts option={mergedOptions} style={{ marginTop: "20px", height: "300px" }} notMerge={true} />
      </Block>
    );
  }

  return null;
};
export default Graph;

const options = JSON.stringify({
  grid: { top: 40, right: 10, bottom: 2, left: 10, containLabel: true },
  // xAxis: {
  //   type: 'category',
  //   data: ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'],
  // },
  yAxis: {
    type: "value",
  },
  series: null,
  tooltip: {
    trigger: "item",
    formatter: `{c}%`,
  },
});

const parseOptions = (options) => {
  return options.map((option) => Object.assign({}, { label: option, value: option }));
};
